import { ComponentProps, FC, MouseEventHandler } from "react";
import { useTranslation } from "react-i18next";
import { PlusIcon } from "@heroicons/react/24/solid";
import { useNavigate } from "react-router-dom";

import { PropsWithClassName } from "@/types/PropsWithClassName";
import { cn } from "@/utils/classNames.ts";
import useTripCreate from "@/api/trips/useTripCreate.ts";
import { useAuthContext } from "@/context/AuthContext.tsx";
import { passToRoute } from "@/utils/passToRoute.ts";
import Routes from "@/router/Routes.ts";

type NewTripButtonProps = PropsWithClassName<
  Pick<ComponentProps<"button">, "onClick">
> & {
  variant?: "big" | "standard" | "dashboard";
};

const NewTripButton: FC<NewTripButtonProps> = ({
  className,
  onClick,
  variant = "standard",
}) => {
  const { t } = useTranslation();
  const { user, canCreateTrip, getSubscriptionPage } = useAuthContext();
  const navigate = useNavigate();
  const {
    mutateAsync: createTrip,
    isPending: isLoadingTripCreate,
    error: errorTripCreate,
  } = useTripCreate();

  const handleTripCreate: MouseEventHandler<HTMLButtonElement> = (event) => {
    void (async () => {
      try {
        if (isLoadingTripCreate) return;

        if (!canCreateTrip) {
          console.warn("User has reached the trip creation limit.");
          navigate(getSubscriptionPage());
          return;
        }

        const newTrip = await createTrip({
          user_id: user?.id,
        });

        onClick?.(event);

        navigate(
          passToRoute(Routes.TRIP__EDIT, {
            id: newTrip.id,
          }),
        );
      } catch (error) {
        console.error("Error creating trip:", error);
      }
    })();
  };

  const shouldShowTripsRemaining = variant !== "dashboard";
  const tripsRemaining = user?.user_features.trips ?? 0;

  return (
    <button
      type="button"
      onClick={handleTripCreate}
      disabled={isLoadingTripCreate}
      className={cn(
        "group flex w-full cursor-pointer items-center text-left transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:cursor-wait disabled:opacity-60",
        {
          "rounded-xl border border-dashed border-primary/60 bg-primary/[0.04] hover:border-primary hover:bg-primary/10":
            variant !== "dashboard",
          "gap-4 p-5": variant === "big",
          "gap-3 px-4 py-3": variant === "standard",
          "journi-primary-button min-h-[32px] justify-center rounded-md border border-primary px-4 py-2":
            variant === "dashboard",
        },
        className,
      )}
    >
      {variant !== "dashboard" ? (
        <span
          className={cn(
            "flex shrink-0 items-center justify-center rounded-full bg-primary text-white transition-transform group-hover:rotate-90",
            {
              "h-12 w-12": variant === "big",
              "h-9 w-9": variant === "standard",
            },
          )}
        >
          {isLoadingTripCreate ? (
            <span className="loading loading-spinner loading-xs" />
          ) : (
            <PlusIcon
              className={cn({
                "h-6 w-6": variant === "big",
                "h-5 w-5": variant === "standard",
              })}
              aria-hidden="true"
            />
          )}
        </span>
      ) : isLoadingTripCreate ? (
        <span className="loading loading-spinner loading-xs" />
      ) : null}

      <span className={cn("min-w-0", { "flex-1": variant !== "dashboard" })}>
        <span
          className={cn({
            "block font-feature font-bold italic": variant !== "dashboard",
            "text-2xl": variant === "big",
            "text-xl": variant === "standard",
            "font-sans text-xs font-medium text-[var(--ink)]":
              variant === "dashboard",
            "text-error": Boolean(errorTripCreate),
            "text-primary": !errorTripCreate && variant !== "dashboard",
          })}
        >
          {t("tripNavbar.plan_a_new_trip")}
          {variant === "dashboard" ? " →" : null}
        </span>

        {shouldShowTripsRemaining ? (
          <span
            className={cn("mt-0.5 block font-sans font-normal not-italic", {
              "text-sm": variant === "big",
              "text-xs": variant === "standard",
              "text-text-secondary": !errorTripCreate,
              "text-error": Boolean(errorTripCreate),
            })}
          >
            {tripsRemaining === 1
              ? t("tripNavbar.trip_remaining", { count: tripsRemaining })
              : t("tripNavbar.trips_remaining", { count: tripsRemaining })}
          </span>
        ) : null}
      </span>
    </button>
  );
};

export default NewTripButton;